import type { ImageParams } from 'components/Common/ResponsiveImage'
import type { ImageChangeHandler, ImageCarouselRefInterface, ImageLoadHandler } from 'LuxKit/Carousel/BaseImageCarousel'
import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import { useMetaSearchMobileV2Enabled } from 'components/Pages/HotelSearchPage/useMetaSearchMobileV2'
import { useAppSelector } from 'hooks/reduxHooks'
import type { PropsWithChildren, ReactElement } from 'react'
import { useCallback, useRef } from 'react'
import styled from 'styled-components'
import { selectLoggedIn } from 'selectors/accountSelectors'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import { useScreenSizeUp } from 'hooks/useScreenSize'

const Container = styled.div`
  grid-area: hero;
`

interface Props extends PropsWithChildren {
  bookmarkAction?: ReactElement
  images: Array<App.Image>
  productLabel?: ReactElement | false
  imageParams: ImageParams
  /** First carousel image eager + fetchPriority high; other slides lazy-managed (BaseImageCarousel). */
  eagerLoadFirstImage?: boolean
  onImageChange?: ImageChangeHandler
  onImageLoad?: ImageLoadHandler
}

function SearchImageCarouselWrapper(props: Props) {
  const {
    bookmarkAction,
    images,
    productLabel,
    imageParams,
    eagerLoadFirstImage,
    onImageChange,
    onImageLoad,
  } = props
  const isMetaSearchMobileV2 = useMetaSearchMobileV2Enabled()
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const hideBookmark = isMetaSearchMobileV2 && !isLoggedIn
  const carouselRef = useRef<ImageCarouselRefInterface | null>(null)

  const isInHoverState = useRef(false) // Are we in hover-induced state?
  const isDesktop = useScreenSizeUp('tablet')
  const isHoverEnabled = isDesktop

  const handleImageChange = useCallback<ImageChangeHandler>((index, image) => {
    if (image) {
      // reset hover state if the image is not the first one
      if (isInHoverState.current && index != 1) {
        isInHoverState.current = false
      }
    }
    onImageChange?.(index, image)
  }, [onImageChange])

  const handleMouseEnter = useCallback(() => {
    // Only trigger hover if on first image and we have a second image
    if (isHoverEnabled && images.length > 1 && carouselRef.current?.getCurrentImageIndex() === 0) {
      isInHoverState.current = true
      carouselRef.current.goToImage(images[1], 'smooth')
    }
  }, [isHoverEnabled, images, carouselRef])

  const handleMouseLeave = useCallback(() => {
    // Only reset if we're still in hover state (user didn't manually interact)
    if (isHoverEnabled && isInHoverState.current && carouselRef.current) {
      carouselRef.current?.goToImage(images[0], 'smooth')
    }

    // Clean up hover state
    isInHoverState.current = false
  }, [isHoverEnabled, images, carouselRef])

  return <Container
    className={SearchImageCarouselWrapper.name}
    onMouseEnter={handleMouseEnter}
    onMouseLeave={handleMouseLeave}
  >
    <ImageCarousel
      carouselRef={carouselRef}
      topLeft={productLabel}
      topRight={bookmarkAction && !hideBookmark ? <TripGuard>{bookmarkAction}</TripGuard> : undefined}
      images={images}
      arrowBehaviour="interaction-visible"
      imageParams={imageParams}
      eagerLoadFirstImage={eagerLoadFirstImage}
      onImageChange={handleImageChange}
      onImageLoad={onImageLoad}
    />
  </Container>
}

export default SearchImageCarouselWrapper
